import { supabase } from "@/integrations/supabase/client";
import type { MvpBundle } from "@/hooks/use-mvp-persistence";
import { parseWorkTypes, WORK_TYPE_LABELS } from "@/types/mvpEstimate";

function normalizeClientKey(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, " ");
}

function cityFromAddress(address: string): string {
  const trimmed = address.trim();
  if (!trimmed) return "—";
  const parts = trimmed.split(",").map((p) => p.trim()).filter(Boolean);
  if (parts.length >= 2) return parts[parts.length - 1]!;
  const postalMatch = trimmed.match(/\d{2}-\d{3}\s+(.+)/);
  if (postalMatch?.[1]) return postalMatch[1].trim();
  return parts[0] ?? trimmed.slice(0, 80);
}

function projectNameFromClient(clientName: string, address: string, bundle: MvpBundle): string {
  const desc = bundle.wizard.projectDescription?.trim();
  if (desc) return desc;
  const city = cityFromAddress(address);
  if (city && city !== "—") return `${clientName} · ${city}`;
  return clientName;
}

function projectTypeLabel(bundle: MvpBundle): string {
  return parseWorkTypes(bundle.wizard.workType)
    .map((t) => WORK_TYPE_LABELS[t])
    .join(", ") || "Remont";
}

function totalAreaM2(bundle: MvpBundle): number {
  return bundle.wizard.rooms.reduce((sum, r) => sum + (r.areaM2 > 0 ? r.areaM2 : 0), 0) || 1;
}

/**
 * Przy generowaniu oferty (krok 3): przypisz istniejący projekt klienta lub utwórz nowy.
 * Wymaga imienia/nazwy i adresu klienta.
 */
export async function ensureDraftProjectAssignment(params: {
  userId: string;
  draftId: string;
  clientName: string;
  clientAddress: string;
  bundle: MvpBundle;
}): Promise<{ projectId: string | null; error?: string }> {
  const { userId, draftId, clientName, clientAddress, bundle } = params;
  const nameKey = normalizeClientKey(clientName);
  const addrKey = normalizeClientKey(clientAddress);
  if (!nameKey || !addrKey) return { projectId: null };

  const { data: draftRow, error: draftFetchError } = await supabase
    .from("drafts")
    .select("project_id")
    .eq("id", draftId)
    .eq("user_id", userId)
    .maybeSingle();

  if (draftFetchError) return { projectId: null, error: draftFetchError.message };

  const existingProjectId = (draftRow as { project_id?: string | null } | null)?.project_id;
  if (existingProjectId) return { projectId: existingProjectId };

  const { data: projects, error: projectsError } = await supabase
    .from("projects")
    .select("id, client_name, description")
    .eq("user_id", userId)
    .order("created_at", { ascending: false })
    .limit(200);

  if (projectsError) return { projectId: null, error: projectsError.message };

  const matched = (projects ?? []).find((p) => {
    const pName = normalizeClientKey(p.client_name ?? "");
    if (pName && pName === nameKey) return true;
    const pDesc = normalizeClientKey(p.description ?? "");
    return pDesc === addrKey;
  });

  let projectId = matched?.id ?? null;

  if (!projectId) {
    const { data: inserted, error: insertError } = await supabase
      .from("projects")
      .insert({
        user_id: userId,
        name: projectNameFromClient(clientName.trim(), clientAddress.trim(), bundle),
        client_name: clientName.trim(),
        city: cityFromAddress(clientAddress),
        project_type: projectTypeLabel(bundle),
        area_m2: totalAreaM2(bundle),
        description: clientAddress.trim(),
        status: "draft",
      })
      .select("id")
      .single();

    if (insertError || !inserted) {
      return { projectId: null, error: insertError?.message ?? "Nie udało się utworzyć projektu." };
    }
    projectId = inserted.id;
  }

  const { error: updateError } = await supabase
    .from("drafts")
    .update({ project_id: projectId })
    .eq("id", draftId)
    .eq("user_id", userId);

  if (updateError) return { projectId: null, error: updateError.message };
  return { projectId };
}
